import { useMemo } from 'react';
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { remarkWikilinks, wikilinkTarget, type WikilinkResolver } from '../lib/wikilinks';
import { WIKILINK_PROTOCOL } from '../lib/wikilinks';

interface MarkdownProps {
  /** Raw Markdown text to render. */
  content: string;
  /** Extra classes for the root wrapper so each caller sets its own type size. */
  className?: string;
  /**
   * Resolves a `[[wikilink]]` target to an app route (document surfaces pass
   * `useWikilinkResolver()`). Absent — or when a target resolves to nothing —
   * the link renders as calm muted text, never broken navigation.
   */
  resolveWikilink?: WikilinkResolver;
}

function cx(...parts: (string | undefined | false)[]): string {
  return parts.filter(Boolean).join(' ');
}

/** True for http(s) hrefs, which get external-link safety attributes. */
function isHttpHref(href: unknown): href is string {
  return typeof href === 'string' && /^https?:\/\//i.test(href);
}

/**
 * react-markdown v9/v10 component-prop signature: each renderer receives the
 * standard element props plus `node`. We destructure only what we style and
 * spread the rest so GFM extras (e.g. task-list classNames) survive.
 */
const components: Components = {
  p: ({ node: _node, children, ...props }) => (
    <p className="leading-relaxed" {...props}>
      {children}
    </p>
  ),
  h1: ({ node: _node, children, ...props }) => (
    <h1 className="text-base font-semibold text-[var(--text-primary)] mt-1" {...props}>
      {children}
    </h1>
  ),
  h2: ({ node: _node, children, ...props }) => (
    <h2 className="text-base font-semibold text-[var(--text-primary)]" {...props}>
      {children}
    </h2>
  ),
  h3: ({ node: _node, children, ...props }) => (
    <h3 className="text-sm font-semibold text-[var(--text-primary)]" {...props}>
      {children}
    </h3>
  ),
  h4: ({ node: _node, children, ...props }) => (
    <h4 className="text-sm font-semibold text-[var(--text-secondary)]" {...props}>
      {children}
    </h4>
  ),
  h5: ({ node: _node, children, ...props }) => (
    <h5 className="text-sm font-semibold text-[var(--text-secondary)]" {...props}>
      {children}
    </h5>
  ),
  h6: ({ node: _node, children, ...props }) => (
    <h6 className="text-sm font-semibold text-[var(--text-secondary)]" {...props}>
      {children}
    </h6>
  ),
  ul: ({ node: _node, children, className, ...props }) => {
    const isTaskList = typeof className === 'string' && className.includes('contains-task-list');
    return (
      <ul
        className={cx(isTaskList ? 'list-none pl-0' : 'list-disc pl-5 space-y-1', className)}
        {...props}
      >
        {children}
      </ul>
    );
  },
  ol: ({ node: _node, children, className, ...props }) => (
    <ol className={cx('list-decimal pl-5 space-y-1', className)} {...props}>
      {children}
    </ol>
  ),
  li: ({ node: _node, children, className, ...props }) => {
    const isTaskItem = typeof className === 'string' && className.includes('task-list-item');
    return (
      <li
        className={cx(
          isTaskItem ? 'list-none flex items-start gap-2' : 'leading-relaxed',
          className
        )}
        {...props}
      >
        {children}
      </li>
    );
  },
  input: ({ node: _node, type, ...props }) => {
    if (type === 'checkbox') {
      return (
        <input
          type="checkbox"
          disabled
          className="mt-1 shrink-0 accent-[var(--primary)] cursor-default"
          {...props}
          onChange={undefined}
        />
      );
    }
    return <input type={type} {...props} />;
  },
  code: ({ node: _node, children, className, ...props }) => (
    <code
      className={cx(
        'rounded px-1.5 py-0.5 text-[0.85em] font-mono bg-[var(--surface-2)] text-[var(--text-primary)] border border-[var(--border)]',
        className
      )}
      {...props}
    >
      {children}
    </code>
  ),
  pre: ({ node: _node, children, ...props }) => (
    <pre
      className="overflow-x-auto rounded-lg p-3 bg-[var(--surface-2)] border border-[var(--border)] text-sm [&_code]:bg-transparent [&_code]:border-0 [&_code]:p-0 [&_code]:text-[0.85em] [&_code]:font-mono"
      {...props}
    >
      {children}
    </pre>
  ),
  blockquote: ({ node: _node, children, ...props }) => (
    <blockquote
      className="border-l-2 border-[var(--border)] pl-3 text-[var(--text-secondary)] italic"
      {...props}
    >
      {children}
    </blockquote>
  ),
  // `a` is intentionally NOT in this map: it is built per render by
  // buildAnchorRenderer so wikilink hrefs can consult the caller's resolver.
  hr: ({ node: _node, ...props }) => (
    <hr className="border-0 border-t border-[var(--border)] my-2" {...props} />
  ),
  // Render images as a plain text link to the src rather than a live <img>, to
  // avoid tracking-pixel/privacy vectors in untrusted model output.
  img: ({ node: _node, src, alt }) => {
    const href = typeof src === 'string' ? src : '';
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow ugc"
        className="text-[var(--primary)] underline underline-offset-2 hover:text-[var(--primary-hover)] rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface)]"
      >
        {alt || href || 'image'}
      </a>
    );
  },
  table: ({ node: _node, children, ...props }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse" {...props}>
        {children}
      </table>
    </div>
  ),
  th: ({ node: _node, children, ...props }) => (
    <th
      className="border border-[var(--border)] px-2 py-1 text-left font-semibold bg-[var(--surface-2)]"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ node: _node, children, ...props }) => (
    <td className="border border-[var(--border)] px-2 py-1 text-left" {...props}>
      {children}
    </td>
  ),
};

const ANCHOR_CLASSES =
  'text-[var(--primary)] underline underline-offset-2 hover:text-[var(--primary-hover)] rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface)]';

/**
 * The anchor renderer, built per render so `[[wikilinks]]` (emitted by
 * remarkWikilinks as `wikilink:` hrefs) can consult the caller's resolver: a
 * resolved target navigates in-app; an unresolved target — or a surface with
 * no resolver, like insights and agent replies — renders as calm muted text,
 * never a dead link. Ordinary hrefs keep the external-link safety attributes.
 */
function buildAnchorRenderer(resolveWikilink?: WikilinkResolver): Components['a'] {
  return function Anchor({ node: _node, children, href, ...props }) {
    const target = wikilinkTarget(href);
    if (target !== null) {
      const route = resolveWikilink?.(target) ?? null;
      if (route) {
        return (
          <Link to={route} className={ANCHOR_CLASSES}>
            {children}
          </Link>
        );
      }
      return <span className="text-[var(--text-secondary)]">{children}</span>;
    }
    const external = isHttpHref(href);
    return (
      <a
        href={href}
        className={ANCHOR_CLASSES}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer nofollow ugc' } : {})}
        {...props}
      >
        {children}
      </a>
    );
  };
}

/**
 * Shared Markdown renderer for document bodies, insights, and agent replies.
 * Wraps react-markdown with GFM (tables, task lists, autolinks) plus wikilink
 * parsing, and styles every block to the design tokens in index.css. Raw HTML
 * is intentionally NOT enabled (no rehype-raw / no dangerouslySetInnerHTML):
 * untrusted user/model text stays escaped and inert.
 */
export function Markdown({ content, className, resolveWikilink }: MarkdownProps) {
  const mergedComponents = useMemo<Components>(
    () => ({ ...components, a: buildAnchorRenderer(resolveWikilink) }),
    [resolveWikilink]
  );
  return (
    <div
      className={cx('space-y-3 text-[var(--text-primary)] leading-relaxed break-words', className)}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkWikilinks]}
        components={mergedComponents}
        // The default transform strips unknown protocols; our wikilink
        // pseudo-protocol must survive to reach the anchor renderer (which
        // never emits it as a real href).
        urlTransform={(url) => (url.startsWith(WIKILINK_PROTOCOL) ? url : defaultUrlTransform(url))}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
